import {
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useCallback, useMemo, useState } from 'react';
import { OnDragEndParams } from './dragAndDrop.types';

const DRAG_ACTIVATION_DISTANCE = 8;

type UseSortableContextProps<T> = {
  items: T[];
  uniqueIdentifier: keyof T;
  onDragEnd?: (props: OnDragEndParams) => void;
  onUpdateItems: (newItems: T[]) => void;
};

const useSortableContext = <T,>({
  items,
  uniqueIdentifier,
  onDragEnd,
  onUpdateItems,
}: UseSortableContextProps<T>) => {
  const [activeId, setActiveId] = useState<string>();

  const itemIds = useMemo(
    () => items.map((item) => item[uniqueIdentifier] as string),
    [items, uniqueIdentifier],
  );

  const activeIndex = useMemo(
    () => itemIds.findIndex((id) => id === activeId),
    [activeId, itemIds],
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      const isSamePosition = !over || active?.id === over?.id;

      if (isSamePosition) {
        return;
      }

      const oldIndex = itemIds.indexOf(active.id as string);
      const newIndex = itemIds.indexOf(over?.id as string);

      const newItems = arrayMove(items, oldIndex, newIndex);

      setActiveId(undefined);

      onUpdateItems(newItems);

      if (onDragEnd) {
        onDragEnd({ activeId: active.id as string, oldIndex, newIndex });
      }
    },
    [itemIds, items, onDragEnd, onUpdateItems],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  return {
    sensors,
    onDragStart,
    onDragEnd: handleDragEnd,
    activeId,
    activeIndex,
    onDragCancel: () => setActiveId(undefined),
  };
};

export default useSortableContext;
